# Exporting questionnaire submissions

Partners can export questionnaire submissions as part of the candidate application workflow.
This functionality is supported for questionnaires created:

- [Programmatically through the SEEK API](/use-cases/job-posting/questionnaires)

- Manually through the direct job posting flow on the SEEK Employer website,
  or the now-retired screen questionnaires page

## Before you begin

Questionnaire submission exports require a [partner token](/auth/partner-tokens).

## Data structure

```mermaid Questionnaire submission objects
erDiagram
        CandidateProfile ||--o| QuestionnaireSubmission: contains
        QuestionnaireSubmission }|--|| Questionnaire : "corresponds to"
        QuestionnaireSubmission ||--|{ ComponentResponse : contains
        ComponentResponse }|--|| Component : "corresponds to"
```

A job ad may specify a [questionnaire](/use-cases/job-posting/questionnaires) to include in SEEK’s Apply Form.
The candidate’s responses to the questionnaire are surfaced through the [`seekQuestionnaireSubmission` field] on the application profile.

The [`ApplicationQuestionnaireSubmission`] object allows you to retrieve:

- The `questionnaire` that the submission corresponds to
- The list of `responses` to the components of the questionnaire
- The `component` that a given response corresponds to

### Questionnaire association

If you manage questionnaires as a holistic collection in your software,
retrieve `questionnaire.id` to correlate the submission back to the questionnaire ID you have stored from the [Job Posting] use case.

If you manage individual questionnaire components in your software,
provide a unique `value` for each [`ApplicationQuestionnaireComponentInput`] during questionnaire creation.
You can then select `responses[].component.value` from the submission to correlate each response back to the corresponding questionnaire component in your software.

### Preferred choices & scoring

Each `SingleSelect` and `MultiSelect` [`ApplicationQuestion`] specifies a collection of possible choices through the `responseChoice` field.
An [`ApplicationQuestionChoice`] can be preferenced by setting its `preferredIndicator` during questionnaire creation.
Candidate responses are scored against these preferred choices.

- The [`ApplicationQuestionnaireSubmission`] schema definition details scoring of the overall questionnaire submission
- The [`ApplicationQuestionResponse`] schema definition details scoring of individual questionnaire components

These scores are provided as an advisory screening indicator for the hirer.
All candidate applications will be delivered to your software;
SEEK does not support knockout questions and does not reject applications based on score.

## Operations

Use the [`candidateProfile` query] to retrieve questionnaire submissions.
You should combine this with the query you use to retrieve [candidate profiles] and [attachments].

### candidateProfile

```graphql
query ($id: String!) {
  candidateProfile(id: $id) {
    seekQuestionnaireSubmission {
      questionnaire {
        id {
          value
        }
      }
      responses {
        componentTypeCode
        ... on ApplicationQuestionResponse {
          score
          component {
            value
            questionHtml
          }
          answers {
            answer
            choice {
              value
            }
          }
        }
        ... on ApplicationPrivacyConsentResponse {
          component {
            value
            descriptionHtml
          }
          consentGivenIndicator
        }
      }
      score
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:candidateProfile:apply:7DtW6Q68gk2R4okNGhvg1Y"
}
```

```jsonc
{
  "candidateProfile": {
    "seekQuestionnaireSubmission": {
      // The questionnaire presented to the candidate as part of their application
      "questionnaire": {
        "id": {
          "value": "seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr"
        }
      },

      // The candidate's answers to the questionnaire
      "responses": [
        {
          "componentTypeCode": "Question",
          "score": 1,
          "component": {
            "value": null,
            "questionHtml": "Which of the following statements best describes your right to work in Australia?"
          },
          "answers": [
            {
              "answer": "I'm an Australian citizen",
              "choice": {
                "value": null
              }
            }
          ]
        },
        {
          "componentTypeCode": "Question",
          "score": 1,
          "component": {
            "value": null,
            "questionHtml": "Do you own or have regular access to a car?"
          },
          "answers": [
            {
              "answer": "No",
              "choice": {
                "value": null
              }
            }
          ]
        },
        {
          "componentTypeCode": "Question",
          "score": null,
          "component": {
            "value": null,
            "questionHtml": "What do you like most about GraphQL?"
          },
          "answers": [
            {
              "answer": "Querying deeply nested objects 😀",
              "choice": null
            }
          ]
        },
        {
          "componentTypeCode": "PrivacyConsent",
          "component": {
            "value": null,
            "descriptionHtml": "Do you agree with our privacy policy?"
          },
          "consentGivenIndicator": true
        }
      ],

      // The overall score of the questionnaire submission
      "score": 1
    }
  }
}
```

[`applicationquestion`]: /schema/named-type/ApplicationQuestion
[`applicationquestionchoice`]: /schema/named-type/ApplicationQuestionChoice
[`applicationquestionnairecomponentinput`]: /schema/named-type/ApplicationQuestionnaireComponentInput
[`applicationquestionnairesubmission`]: /schema/named-type/ApplicationQuestionnaireSubmission
[`applicationquestionresponse`]: /schema/named-type/ApplicationQuestionResponse
[`candidateprofile` query]: /schema/query/candidateProfile
[`seekquestionnairesubmission` field]: /schema/named-type/CandidateProfile#/field/seekQuestionnaireSubmission
[attachments]: /use-cases/application-export/attachments
[candidate profiles]: /use-cases/application-export/candidate-profiles
[job posting]: /use-cases/job-posting
